<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('distribution-channel.company')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownCompanies"
            :selected-item="selectedCompany"
            @update:selected="handleCompanySelection"
            name="company"
            :mode="mode"
            show-error-as-tooltip
            :error="companyErrorMessage" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('material.mwAccountGroup')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownMwAccountGroups"
            :selected-item="selectedMWAccountGroup"
            @update:selected="handleMWAccountGroupSelection"
            name="mwAccountGroup"
            :mode="mode"
            show-error-as-tooltip
            :error="mwAccountGroupErrorMessage"
            clearable />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('distribution-channel.isDefault')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck v-if="mode == 'view'" :item="data.isDefault" />
          <BaseToggle v-else name="isDefault" v-model="data.isDefault" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('distribution-channel.exvan')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck v-if="mode == 'view'" :item="data.exvan" />
          <BaseToggle v-else name="exvan" v-model="data.exvan" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('distribution-channel.orders')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck v-if="mode == 'view'" :item="data.orders" />
          <BaseToggle v-else name="orders" v-model="data.orders" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('distribution-channel.logistics')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck v-if="mode == 'view'" :item="data.logistics" />
          <BaseToggle v-else name="logistics" v-model="data.logistics" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('distribution-channel.uniqueTradersPerRoute')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck v-if="mode == 'view'" :item="data.uniqueTradersPerRoute" />
          <BaseToggle v-else name="uniqueTradersPerRoute" v-model="data.uniqueTradersPerRoute" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive" />
          <BaseToggle v-else name="isActive" v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : 0" mode="view" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <p class="text-sm font-normal text-gray-900">
            {{ data?.createdDate ? isoDateToFullDate(data?.createdDate) : '-' }}
          </p>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <p class="text-sm font-normal text-gray-900">
            {{ data?.lastUpdate ? isoDateToFullDate(data?.lastUpdate) : '-' }}
          </p>
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import BaseDetailsTableRow from '@/components/common/BaseDetailsTableRow.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseTableCellStatus from '@/components/common/BaseTableCellStatus.vue';
import BaseTableCellCheck from '@/components/common/BaseTableCellCheck.vue';
import { isoDateToFullDate } from '@/utils/date';
import type { DistributionChannel } from '@/types/DistributionChannel';
import type { SelectDropdownItem } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();
const { companies, mwAccountGroups, dropdownMwAccountGroups, dropdownCompanies } =
  storeToRefs(useLookupsStore());

const data = defineModel<DistributionChannel>();

defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  companyErrorMessage?: string;
  mwAccountGroupErrorMessage?: string;
}>();

const selectedCompany = computed(
  () => dropdownCompanies.value.find((item) => item.value === data.value?.companyID) || null
);

function handleCompanySelection(item: SelectDropdownItem | null) {
  if (data.value) {
    data.value.companyID = item?.value.toString() || '';
    data.value.company = companies.value.find((c) => c.id === item?.value) || {
      id: '',
      name: '',
    };
  }
}

const selectedMWAccountGroup = computed(
  () =>
    dropdownMwAccountGroups.value.find((item) => item.value === data.value?.mwAccountGroupID) ||
    null
);

function handleMWAccountGroupSelection(item: SelectDropdownItem | null) {
  if (data.value) {
    data.value.mwAccountGroupID = item?.value.toString() || '';
    if (item) {
      const group = mwAccountGroups.value.find((g) => g.id === item.value);
      if (group) {
        data.value.mwAccountGroup = {
          id: group.id,
          accountGroup: group.accountGroup,
          mwEntity: group.mwEntity,
        };
      }
    }
  }
}
</script>
